import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatCurrency } from "../lib/currency";
import OrgInfoSection from "./org/OrgInfoSection";
import PlansSection from "./org/PlansSection";
import OidcSection from "./org/OidcSection";
import SamlSection from "./org/SamlSection";
import TeamSection from "./org/TeamSection";
import CoachSettingsSection from "./org/CoachSettingsSection";
import SeatTrialBanner from "./org/SeatTrialBanner";
import RenewalWarningBanner from "./org/RenewalWarningBanner";
import PaymentMethodSection from "./org/PaymentMethodSection";
import BillingTimeline from "./org/BillingTimeline";
import CustomRolesSection from "./org/CustomRolesSection";
import UserAccessPanel from "./org/UserAccessPanel";
import DepartmentModulesPanel from "./org/DepartmentModulesPanel";
import HierarchyPanel from "./org/HierarchyPanel";
import EntitlementsPanel from "./org/EntitlementsPanel";
import HolidaysPanel from "./org/HolidaysPanel";

const EMPTY_OIDC = { provider_name: "azure_ad", discovery_url: "", client_id: "", client_secret: "", scope: "openid profile email" };
const EMPTY_SAML = { provider_name: "okta", idp_entity_id: "", idp_sso_url: "", idp_slo_url: "", idp_x509_cert: "", attribute_mapping: { email: "email", full_name: "name" } };

export default function OrgPage() {
  const { user, refreshMe } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "superuser");
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [oidc, setOidc] = useState(null);
  const [saml, setSaml] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [stripeBusy, setStripeBusy] = useState(false);
  const [oidcForm, setOidcForm] = useState(EMPTY_OIDC);
  const [samlForm, setSamlForm] = useState(EMPTY_SAML);

  const refresh = useCallback(async () => {
    const [t, p, u, inv, oc, sa] = await Promise.all([
      api.get("/tenant").then((r) => r.data),
      api.get("/subscription/plans").then((r) => r.data),
      api.get("/tenant/users").then((r) => r.data),
      api.get("/tenant/invitations").then((r) => r.data).catch(() => []),
      api.get("/tenant/oidc").then((r) => r.data).catch(() => null),
      api.get("/tenant/saml").then((r) => r.data).catch(() => null),
    ]);
    setTenant(t); setPlans(p); setUsers(u); setInvitations(inv); setOidc(oc); setSaml(sa);
  }, []);

  useEffect(() => { if (isAdmin) refresh().catch(() => {}); }, [refresh, isAdmin]);

  const changePlan = async (plan) => {
    try {
      const { data } = await api.post("/subscription/change", { plan });
      const cur = tenant?.preferred_currency || "USD";
      if (data.charged) {
        toast.success(`Upgraded to ${plan} · charged ${formatCurrency(data.prorated_amount, cur)} for ${data.days_remaining} days remaining.`);
      } else if (Number(data.credit_applied) > 0) {
        toast.success(`Switched to ${plan}. ${formatCurrency(data.credit_applied, cur)} credit banked for next renewal.`);
      } else {
        toast.success(`Switched to ${plan} plan`);
      }
      await refreshMe();
      refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Auto-redirect the "checkout_required" 402 path straight into a Tap checkout for the target plan
      if (err.response?.status === 402 && detail?.code === "checkout_required" && detail?.new_plan) {
        toast.info("Redirecting to Tap to complete the upgrade…");
        try {
          const { data: co } = await api.post("/tap/charges/checkout", { intent: "plan", plan: detail.new_plan });
          if (co?.transaction_url) {
            window.location.href = co.transaction_url;
            return;
          }
          toast.error("Tap did not return a redirect URL — please retry.");
          return;
        } catch (chErr) {
          toast.error(formatApiError(chErr.response?.data?.detail) || "Could not start Tap checkout.");
          return;
        }
      }
      toast.error(formatApiError(detail) || "Plan change failed.");
    }
  };

  const startStripeCheckout = async (plan) => {
    setStripeBusy(true);
    try {
      const { data } = await api.post("/tap/charges/checkout", { intent: "plan", plan });
      if (data.transaction_url) {
        window.location.href = data.transaction_url;
      } else {
        toast.error("Tap did not return a redirect URL — please retry.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Payment checkout unavailable — use the SWITCH button for instant in-app plan change.");
    } finally { setStripeBusy(false); }
  };

  const openStripePortal = async () => {
    // Tap doesn't offer a hosted portal like Stripe. Direct users to their profile / billing page instead.
    toast.info("Manage subscription from the Org page — cancellations & refunds are handled by our support team.");
  };

  const saveOidc = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tenant/oidc", oidcForm);
      toast.success(`SSO configured. Login URL: ${data.login_url}`);
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeOidc = async () => {
    await api.delete("/tenant/oidc");
    toast.success("SSO config removed");
    refresh();
  };

  const saveSaml = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tenant/saml", samlForm);
      toast.success(`SAML configured. Metadata: ${data.metadata_url}`);
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeSaml = async () => {
    await api.delete("/tenant/saml");
    toast.success("SAML config removed");
    refresh();
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/tenant/invitations", { email: inviteEmail, role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      navigator.clipboard?.writeText(data.invite_link).catch(() => {});
      setInviteEmail("");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="org-page">
      <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 09 · ORGANIZATION</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Organization.</h1>

      {!isAdmin && user ? (
        <div className="mt-8 border border-zinc-950 p-8 max-w-xl" data-testid="org-non-admin-lock">
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#D0021B] mb-2">RESTRICTED</div>
          <h2 className="text-xl font-semibold mb-2">Admins only</h2>
          <p className="text-sm text-zinc-600">
            Organization settings, subscription plans, team invites, SSO, and access control are managed by your tenant admin.
            Ask your admin for access if you need to change anything here.
          </p>
        </div>
      ) : (
        <>
      <OrgInfoSection tenant={tenant} />
      <RenewalWarningBanner tenant={tenant} />
      <SeatTrialBanner tenant={tenant} />
      <PlansSection
        plans={plans}
        tenant={tenant}
        stripeBusy={stripeBusy}
        onChangePlan={changePlan}
        onStripeCheckout={startStripeCheckout}
        onOpenPortal={openStripePortal}
      />
      <OidcSection oidc={oidc} form={oidcForm} onFormChange={setOidcForm} onSave={saveOidc} onRemove={removeOidc} />
      <SamlSection saml={saml} form={samlForm} onFormChange={setSamlForm} onSave={saveSaml} onRemove={removeSaml} />
      <PaymentMethodSection tenant={tenant} onRefresh={refresh} />
      <BillingTimeline />
      <TeamSection
        users={users}
        invitations={invitations}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        onSendInvite={sendInvite}
        onReload={refresh}
      />
      <CoachSettingsSection isAdmin={user?.role === "admin" || user?.role === "superuser"} />
      <CustomRolesSection tenantModules={tenant?.subscription?.modules || undefined} />
      <UserAccessPanel tenantModules={tenant?.subscription?.modules || []} />
      <DepartmentModulesPanel tenantModules={tenant?.subscription?.modules || []} />
      <HierarchyPanel />
      <EntitlementsPanel />
      <HolidaysPanel />
        </>
      )}
    </div>
  );
}
